import React from 'react';
import { X, Zap, Check, DollarSign, Maximize, Calendar } from 'lucide-react';
import { Slot } from '../types';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities

interface SlotDetailModalProps {
  slot: Slot | null;
  isOpen: boolean;
  onClose: () => void;
  onReserve?: (slot: Slot) => void;
}

export default function SlotDetailModal({ slot, isOpen, onClose, onReserve }: SlotDetailModalProps) {
  if (!isOpen || !slot) return null;

  const getSlotTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'premium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'preferred':
        return 'bg-moonbone text-ink border-teal-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Slot {slot.slot_number}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Type and Status Badges */}
          <div className="flex flex-wrap gap-2">
            <span
              className={`inline-block px-3 py-1 text-sm font-medium rounded-full border ${getSlotTypeColor(
                slot.slot_type
              )}`}
            >
              {slot.slot_type.charAt(0).toUpperCase() + slot.slot_type.slice(1)} Slot
            </span>
            {slot.is_available ? (
              <span className="inline-block px-3 py-1 text-sm font-medium rounded-full bg-teal-100 text-primary-dark border border-teal-200">
                Available
              </span>
            ) : (
              <span className="inline-block px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-800 border border-red-200">
                Reserved
              </span>
            )}
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Price */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center text-gray-600 mb-1">
                <DollarSign className="h-4 w-4 mr-1" />
                <span className="text-sm">Base Price</span>
              </div>
              <p className="text-2xl font-bold text-primary-dark">{formatCurrency(Number(slot.base_price) || 0)} {/* Issue #905: Decimal.js formatting */}</p>
            </div>

            {/* Dimensions */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center text-gray-600 mb-1">
                <Maximize className="h-4 w-4 mr-1" />
                <span className="text-sm">Dimensions</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {slot.dimensions || 'Not specified'}
              </p>
            </div>

            {/* Power */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center text-gray-600 mb-1">
                <Zap className={`h-4 w-4 mr-1 ${slot.has_power ? 'text-yellow-500' : ''}`} />
                <span className="text-sm">Power</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {slot.has_power ? 'Available' : 'Not Available'}
              </p>
            </div>

            {/* Slot Number */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center text-gray-600 mb-1">
                <Calendar className="h-4 w-4 mr-1" />
                <span className="text-sm">Slot ID</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">{slot.slot_number}</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">About This Slot</h3>
            <p className="text-gray-600">
              {slot.slot_type === 'premium'
                ? 'Premium booth space with prime location and enhanced visibility. Ideal for established vendors seeking maximum exposure.'
                : slot.slot_type === 'preferred'
                ? 'Preferred booth space with good foot traffic and visibility. Great for vendors looking for a balance of location and value.'
                : 'Standard booth space suitable for all vendor types. A great option for new and returning vendors.'}
            </p>
          </div>

          {/* Features List */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Features</h3>
            <ul className="space-y-2">
              <li className="flex items-center text-sm text-gray-600">
                <Check className="h-4 w-4 mr-2 text-teal-500" />
                {slot.dimensions ? `${slot.dimensions} booth space` : 'Standard booth space'}
              </li>
              <li className="flex items-center text-sm text-gray-600">
                <Check className="h-4 w-4 mr-2 text-teal-500" />
                Setup and teardown support
              </li>
              {slot.has_power && (
                <li className="flex items-center text-sm text-gray-600">
                  <Check className="h-4 w-4 mr-2 text-teal-500" />
                  Electrical outlet access
                </li>
              )}
              <li className="flex items-center text-sm text-gray-600">
                <Check className="h-4 w-4 mr-2 text-teal-500" />
                Event signage and wayfinding
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
          {slot.is_available && (
            <button
              onClick={() => onReserve?.(slot)}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
            >
              Reserve This Slot
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
